import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { useTikTokStore } from '@/stores/integrations/tiktok-store';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';

interface TikTokIntegrationProps {
  id: string | null;
  isConnected: boolean;
  metadata?: {
    name?: string;
    username?: string;
  };
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

/**
 * TikTok Integration Component
 *
 * Handles TikTok account connection using TikTok Business API OAuth flow.
 * Unlike Instagram, TikTok uses a single-step OAuth flow (no page selection needed).
 */
const TikTokIntegration = (props: TikTokIntegrationProps) => {
  const { t } = useTranslation();
  const { id, isConnected, metadata, variant, detailHref } = props;
  const { user } = useAuth();
  const brandId = user?.currentBrand?.id;
  const queryClient = useQueryClient();

  const { isLoading, disconnect, initiateOAuthFlow } = useTikTokStore();

  useEffect(() => {
    // Handle backend redirect result (backend exchanged the code and redirects here)
    const urlParams = new URLSearchParams(window.location.search);
    const tiktokResult = urlParams.get('tiktok');

    if (tiktokResult === 'connected') {
      toaster.create({
        description: t('integrations.tiktok.success.connected'),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });

      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    if (tiktokResult === 'error') {
      const reason = urlParams.get('reason') || 'unknown';
      toaster.create({
        description: t('integrations.tiktok.errors.connectionFailed'),
        type: 'error',
      });
      console.error('TikTok OAuth failed:', reason);

      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [queryClient, t]);

  const handleToggle = async () => {
    if (isConnected && id) {
      // Handle disconnection
      try {
        await disconnect(id);
        // The card reads the server, so the row only flips once the refreshed
        // list says so.
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        toaster.create({
          description: t('integrations.tiktok.success.disconnected'),
          type: 'success',
        });
      } catch (error) {
        console.error('Failed to disconnect TikTok:', error);
        toaster.create({
          description: t('integrations.tiktok.errors.disconnectFailed'),
          type: 'error',
        });
      }
    } else {
      // Handle connection using OAuth flow
      try {
        await initiateOAuthFlow();
        toaster.create({
          description: t('integrations.tiktok.redirecting'),
          type: 'loading',
        });
      } catch (error) {
        console.error('Failed to initiate TikTok OAuth flow:', error);
        toaster.create({
          description: t('integrations.tiktok.errors.connectionFailed'),
          type: 'error',
        });
      }
    }
  };

  return (
    <IntegrationCard
      name={t('integrations.tiktok.name')}
      description={t('integrations.tiktok.description')}
      icon="https://sf-tb-sg.ibytedtos.com/obj/eden-sg/uhtyvueh7nulogpoguhm/tiktok-icon2.png"
      isActive={isConnected}
      isLoading={isLoading}
      disabled={isLoading}
      onToggle={handleToggle}
      connectedAs={metadata?.username || metadata?.name}
      connectedLabel={t('integrations.connectedLabel')}
      variant={variant}
      detailHref={detailHref}
    />
  );
};

export default TikTokIntegration;
